import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BarcodeDisplay = ({ assetId, serialNumber }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle'); // 'idle', 'success', 'error'

  const handlePrint = async () => {
    setIsPrinting(true);
    // Simulate print process
    setTimeout(() => {
      setIsPrinting(false);
      console.log('Barcode printed for asset:', assetId);
    }, 1500);
  };

  const handleCopy = async () => {
    try {
      // Reset copy status
      setCopyStatus('idle');

      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        // Ensure document is focused
        if (!document.hasFocus()) {
          window.focus();
        }

        await navigator.clipboard?.writeText(assetId);
        setCopyStatus('success');
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = assetId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body?.appendChild(textArea);
        textArea?.focus();
        textArea?.select();

        const successful = document.execCommand('copy');
        document.body?.removeChild(textArea);

        if (successful) {
          setCopyStatus('success');
        } else {
          throw new Error('Copy command failed');
        }
      }

      // Reset status after 2 seconds
      setTimeout(() => setCopyStatus('idle'), 2000);

    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopyStatus('error');

      // Reset status after 2 seconds
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Barcode & QR Code</h3>
        <div className="items-center space-x-2 hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            iconName={copyStatus === 'success' ? 'Check' : 'Copy'}
            iconPosition="left"
            className={`${copyStatus === 'success' ? 'text-green-600' : ''} ${copyStatus === 'error' ? 'text-red-600' : ''}`}>

            {copyStatus === 'success' ? 'Copied!' : copyStatus === 'error' ? 'Failed' : 'Copy ID'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            loading={isPrinting}
            iconName="Printer"
            iconPosition="left">

            Print Label
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Barcode Section */}
        <div className="text-center">
          <div className="bg-white border-2 border-dashed border-border rounded-lg p-6 mb-3">
            {/* Simulated Barcode */}
            <div className="flex justify-center items-end space-x-1 mb-2">
              {Array.from({ length: 20 }, (_, i) =>
              <div
                key={i}
                className="bg-foreground"
                style={{
                  width: Math.random() > 0.5 ? '3px' : '2px',
                  height: `${30 + Math.random() * 20}px`
                }} />

              )}
            </div>
            <p className="text-sm font-mono text-foreground">{assetId}</p>
          </div>
          <p className="text-xs text-muted-foreground">Linear Barcode (Code 128)</p>
        </div>

        {/* QR Code Section */}
        <div className="text-center">
          <div className="bg-white border-2 border-dashed border-border rounded-lg p-6 mb-3 flex justify-center">
            {/* Simulated QR Code */}
            <div className="w-24 h-24 bg-foreground rounded-sm relative">
              <div className="absolute inset-2 bg-white"></div>
              <div className="absolute top-2 left-2 w-4 h-4 bg-foreground"></div>
              <div className="absolute top-2 right-2 w-4 h-4 bg-foreground"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 bg-foreground"></div>
              <div className="absolute inset-4 grid grid-cols-4 gap-1">
                {Array.from({ length: 16 }, (_, i) =>
                <div
                  key={i}
                  className={`${Math.random() > 0.5 ? 'bg-foreground' : 'bg-white'} rounded-sm`} />

                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">QR Code (Asset Details)</p>
        </div>

        {/* Asset Information */}
        <div className="bg-muted rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Asset ID</p>
              <p className="font-mono font-medium text-foreground">{assetId}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Serial Number</p>
              <p className="font-mono font-medium text-foreground">{serialNumber}</p>
            </div>
          </div>
        </div>

        {/* Scanning Instructions */}
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-accent mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Scanning Instructions</p>
              <ul className="text-muted-foreground space-y-1 text-xs">
                <li>• Use barcode scanner for quick asset identification</li>
                <li>• QR code contains full asset details and location</li>
                <li>• Print labels on weather-resistant material</li>
                <li>• Attach labels to visible, accessible locations</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default BarcodeDisplay;